import { FC, useCallback, useContext, useEffect, useMemo } from 'react';
import { Marker } from 'react-leaflet';
import L from 'leaflet';
import { getContentType } from '../common/Common';
import { AppContext } from '../contexts/AppContext';
import useFetchDocument from '../hooks/useFetchDocument';
import {
  buildMapPinHtml,
  MAP_CIRCLE_ANCHOR,
  MAP_CIRCLE_SIZE,
  MAP_PIN_ANCHOR,
  MAP_PIN_SIZE
} from './MapPin';

interface MapMarkerProps {
  documentId: number,
  title?: string,
  subtitle?: string,
  image?: any,
  markerColor?: string,
  markerStyle?: string,
  /** Optional lat/lng from the presentation record (useful in CMS preview) */
  fallbackLocation?: { lat?: number, lng?: number, lon?: number } | null,
  /** Called when the marker's map coordinates are known or cleared */
  onLocation?: (documentId: number, latLng: [number, number] | null) => void,
  /** Called when the marker is tapped */
  onSelect?: (documentId: number, latLng: [number, number], contentType?: string) => void
}

/**
 * Resolve lat/lng from a document or fallback record location.
 * @param data Fetched document
 * @param fallback Optional location from the presentation record
 * @returns Coordinate pair or null
 */
const getDocumentLocation = (
  data: any,
  fallback?: { lat?: number, lng?: number, lon?: number } | null
): [number, number] | null => {
  const location = data?.location || data?.content?.metadata?.location || fallback;
  const lat = Number(location?.lat);
  const lng = Number(location?.lng ?? location?.lon);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  return [lat, lng];
};

/**
 * Render a map marker as a plain Leaflet DivIcon.
 * @returns {JSX.Element | null} Component template
 */
const MapMarker: FC<MapMarkerProps> = ({
  documentId,
  markerColor = "#3F7DF0",
  markerStyle = "mapmarker",
  fallbackLocation,
  onLocation,
  onSelect
}) => {
  const { currentLanguage } = useContext(AppContext);
  const [data] = useFetchDocument(documentId);
  const latLng = useMemo(
    () => getDocumentLocation(data, fallbackLocation),
    [data, fallbackLocation]
  );

  const isAvailableInLanguage = Boolean(
    !data?.status ||
    data?.title?.[currentLanguage || "no"] ||
    data?.content?.content?.blocks?.[currentLanguage || "no"] ||
    // Preview records may resolve coords before full document payload is ready
    (fallbackLocation && latLng)
  );
  const isPublished = Boolean(!data?.status || data?.status?.includes("published"));
  const shouldShow = Boolean(latLng && isPublished && (data ? isAvailableInLanguage : Boolean(fallbackLocation)));
  const isCircle = markerStyle === "circle";

  const icon = useMemo(() => L.divIcon({
    html: buildMapPinHtml(markerColor, markerStyle || "mapmarker"),
    className: "map-marker-icon",
    iconSize: isCircle
      ? [MAP_CIRCLE_SIZE.width, MAP_CIRCLE_SIZE.height]
      : [MAP_PIN_SIZE.width, MAP_PIN_SIZE.height],
    iconAnchor: isCircle
      ? [MAP_CIRCLE_ANCHOR.x, MAP_CIRCLE_ANCHOR.y]
      : [MAP_PIN_ANCHOR.x, MAP_PIN_ANCHOR.y],
  }), [isCircle, markerColor, markerStyle]);

  useEffect(() => {
    if (!onLocation) { return; }
    onLocation(documentId, shouldShow && latLng ? latLng : null);
  }, [documentId, latLng, onLocation, shouldShow]);

  // Clear parent location only when this marker unmounts (e.g. removed in preview)
  useEffect(() => {
    return () => {
      onLocation?.(documentId, null);
    };
  }, [documentId, onLocation]);

  const handleClick = useCallback(() => {
    if (!latLng || !onSelect) { return; }
    onSelect(documentId, latLng, getContentType(data) || "article");
  }, [data, documentId, latLng, onSelect]);

  if (!shouldShow || !latLng) {
    return null;
  }

  return (
    <Marker
      position={latLng}
      icon={icon}
      eventHandlers={{
        click: (event) => {
          L.DomEvent.stopPropagation(event.originalEvent);
          L.DomEvent.preventDefault(event.originalEvent);
          handleClick();
        }
      }}
    />
  );
};

export default MapMarker;
